import {
  ADD_GEOTAG_URL_REQUEST,
  ADD_GEOTAG_URL_SUCCESS,
  ADD_KEYWORD_REQUEST,
  ADD_KEYWORD_SUCCESS,
  CLEAR_SOCIAL_TOKEN_REQUEST,
  CLEAR_SOCIAL_TOKEN_SUCCESS,
  GET_BOARD_MEMBER_LIST_SUCCESS,
  GET_BOARD_META_SUCCESS,
  GET_BOARD_SUCCESS,
  INVITE_BOARD_MEMBERS_SUCCESS,
  REMOVE_BOARD_MEMBER_SUCCESS,
  REMOVE_GEOTAG_URL_REQUEST,
  REMOVE_GEOTAG_URL_SUCCESS,
  REMOVE_KEYWORD_REQUEST,
  SET_ASSOCIATED_FBPAGE_REQUEST,
  SET_ASSOCIATED_FBPAGE_SUCCESS,
  SET_FB_TOKEN_SUCCESS,
  SET_KEYWORDS,
  SET_YOUTUBE_ACC_TOKEN_SUCCESS,
  UPDATE_BOARD_EMBED_URL_SUCCESS,
  UPDATE_BOARD_MEMBER_ROLE_REQUEST,
  UPDATE_BOARD_MEMBER_ROLE_SUCCESS,
  UPDATE_BOARD_SHARE_MESSAGING_SUCCESS,
  UPDATE_BOARD_SUCCESS,
  SET_BOARD_META_REQUEST,
  SET_BOARD_META_SUCCESS,
  SET_TWITTER_ACC_TOKEN_SUCCESS,
  SET_SUBSCRIBE_INSTAGRAM_MENTIONS_REQUEST,
  SET_SUBSCRIBE_INSTAGRAM_MENTIONS_SUCCESS,
  CLEAR_ENTITY,
  BLOCK_BOARD_WORDS_REQUEST,
  UNBLOCK_BOARD_WORDS_REQUEST,
  TOGGLE_BOARD_WORD_BLACKLIST_REQUEST,
  BOARD_UNBLOCK_USERNAME,
  BOARD_BLOCK_USERNAME,
  ADD_FOLDER,
  SET_FOLDER_SETTINGS_REQUEST,
  SET_FOLDER_SETTINGS_SUCCESS,
  SET_FOLDER_META_REQUEST,
  SET_FOLDER_META_SUCCESS,
  DELETE_FOLDER_REQUEST,
  DELETE_FOLDER_SUCCESS,
  PUBLISH_BOARD_SUCCESS,
  REQUIRE_BOARD_WORDS_REQUEST,
  REMOVE_REQUIRED_BOARD_WORD_REQUEST,
  ADD_FOLDER_TO_STORY_SUCCESS,
  REMOVE_FOLDER_FROM_STORY_SUCCESS,
  SET_BOARD,
} from '../constants';
import { createReducer } from './Utils';
import { BoardState, EntityState, FunctionMap } from '../../types';
import { AddKeyword, FSAInterface, RemoveGeotag, RemoveKeyword, SetNewGeotag } from '../actions';
import { GeoLocation } from '../../models/Board';
import set from 'lodash/fp/set';
import get from 'lodash/fp/get';

import {
  addNewEntityMembers,
  clearEntitySocialToken,
  entityAddRequiredWords,
  entityBlockUsername,
  entityBlockWord,
  entityRemoveRequiredWord,
  entityToggleWordBlackList,
  entityUnblockUserName,
  entityUnblockWord,
  removeEntityUser,
  setEntity,
  setEntityAssociatedFBPage,
  setEntityMemberRole,
  setEntityMemberRoleOptimistic,
  setEntityMembers,
  setEntityMeta,
  setEntitySocialToken,
  setEntitySubscribedInstagramMentions,
  updateEntity,
} from './common/Entity';

const initialState = {
  members: [],
  keywords: [],
};

function clearBoard() {
  return initialState;
}

function addNewGeotag(state: BoardState, action: FSAInterface<SetNewGeotag['payload']>) {
  const geoLocations = state.geoLocations.map((geoLocation: GeoLocation) => {
    if (action.payload?._id && !geoLocation._id) {
      return action.payload;
    }

    return geoLocation;
  });

  const updatedGeoLocations = action.payload?._id ? geoLocations : [...geoLocations, action.payload];
  return { ...state, geoLocations: updatedGeoLocations };
}

function removeGeotag(state: BoardState, action: FSAInterface<RemoveGeotag['payload']>) {
  const geoLocations = state.geoLocations.filter((geoLocation: GeoLocation) => geoLocation._id !== action.payload?.id);
  return { ...state, geoLocations };
}

function addKeywordOptimistic(state: BoardState, action: FSAInterface<AddKeyword['payload']>) {
  const keywords = [...state.keywords, { keyword: action.payload?.keyword }];
  return { ...state, keywords };
}

function addKeyword(state: BoardState, action: FSAInterface<AddKeyword['payload']>) {
  const keywordToUpdate = state.keywords.findIndex((keyword) => keyword.keyword === action.payload?.keyword);
  const newKeywords = [...state.keywords];
  if (action.payload) {
    newKeywords.splice(keywordToUpdate, 1, action.payload);
  }
  return { ...state, keywords: newKeywords };
}

function removeKeyword(state: BoardState, action: FSAInterface<RemoveKeyword['payload']>) {
  const keywords = state.keywords.filter((keyword) => keyword._id !== action.payload?.keyword?._id);
  return { ...state, keywords };
}

function setKeywords(state: BoardState, action: any) {
  return { ...state, keywords: [...action.keywords] };
}

function addFolder(state: BoardState, action: any) {
  return set(['folders', action.folder._id], action.folder, state);
}

function setFolderSettings(state: EntityState, action: FSAInterface<{ settings: any }>) {
  const boardId = action.meta.boardId;
  const settings =
    get(['payload', 'data', 'folders', boardId, 'settings'], action) ||
    get(['payload', 'folders', boardId, 'settings'], action);
  const folders = set(['folders', boardId, 'settings'], settings, state);
  return { ...state, ...folders };
}

function setFolderSettingsOptimistic(state: EntityState, action: FSAInterface<{ settings: any }>) {
  const boardId = action.meta.boardId;
  const currentSettings = get(['folders', boardId, 'settings'], state);
  const newSettings = get(['payload', 'settings'], action);
  return set(['folders', boardId, 'settings'], { ...currentSettings, ...newSettings }, state);
}

function setFolderMeta(state: EntityState, action: FSAInterface<{ meta: any }>) {
  const boardId = action.meta.boardId;
  const meta = get(`payload.folders.${boardId}`, action) || get('payload', action);
  const folders = { ...state.folders };
  folders[boardId] = { ...state.folders[boardId], ...meta };
  return { ...state, folders: { ...folders } };
}

function deleteFolder(state: EntityState, action: FSAInterface<{ meta: any }>) {
  const boardId = action.meta.boardId;
  const folders = { ...state.folders };
  delete folders[boardId];
  return { ...state, folders: { ...folders } };
}

function setAddedCount(state: EntityState, action: FSAInterface<{ meta: any }>) {
  const folderId = get('payload.folderId', action);
  const folders = { ...state.folders };
  folders[folderId].addedCount = folders[folderId].addedCount + 1;
  return { ...state, folders: { ...folders } };
}

function setRemovedCount(state: EntityState, action: FSAInterface<{ meta: any }>) {
  const folderId = get('payload.folderId', action);
  const folders = { ...state.folders };
  folders[folderId].addedCount = folders[folderId].addedCount - 1;
  return { ...state, folders: { ...folders } };
}

const functionMap: FunctionMap = {
  [GET_BOARD_MEMBER_LIST_SUCCESS]: setEntityMembers,
  [UPDATE_BOARD_MEMBER_ROLE_REQUEST]: setEntityMemberRoleOptimistic,
  [UPDATE_BOARD_MEMBER_ROLE_SUCCESS]: setEntityMemberRole,
  [REMOVE_BOARD_MEMBER_SUCCESS]: removeEntityUser,
  [INVITE_BOARD_MEMBERS_SUCCESS]: addNewEntityMembers,
  [GET_BOARD_SUCCESS]: setEntity,
  [ADD_GEOTAG_URL_SUCCESS]: addNewGeotag,
  [ADD_GEOTAG_URL_REQUEST]: addNewGeotag,
  [REMOVE_GEOTAG_URL_REQUEST]: removeGeotag,
  [REMOVE_GEOTAG_URL_SUCCESS]: removeGeotag,
  [CLEAR_ENTITY]: clearBoard,
  [UPDATE_BOARD_SUCCESS]: updateEntity,
  [ADD_KEYWORD_REQUEST]: addKeywordOptimistic,
  [ADD_KEYWORD_SUCCESS]: addKeyword,
  [REMOVE_KEYWORD_REQUEST]: removeKeyword,
  [SET_KEYWORDS]: setKeywords,
  [BOARD_UNBLOCK_USERNAME]: entityUnblockUserName,
  [BOARD_BLOCK_USERNAME]: entityBlockUsername,
  [GET_BOARD_META_SUCCESS]: setEntityMeta,
  [UPDATE_BOARD_EMBED_URL_SUCCESS]: setEntityMeta,
  [UPDATE_BOARD_SHARE_MESSAGING_SUCCESS]: setEntityMeta,
  [SET_BOARD_META_REQUEST]: setEntityMeta,
  [SET_BOARD_META_SUCCESS]: setEntityMeta,
  [SET_FB_TOKEN_SUCCESS]: setEntitySocialToken,
  [CLEAR_SOCIAL_TOKEN_REQUEST]: clearEntitySocialToken,
  [CLEAR_SOCIAL_TOKEN_SUCCESS]: clearEntitySocialToken,
  [SET_ASSOCIATED_FBPAGE_REQUEST]: setEntityAssociatedFBPage,
  [SET_ASSOCIATED_FBPAGE_SUCCESS]: setEntityAssociatedFBPage,
  [SET_SUBSCRIBE_INSTAGRAM_MENTIONS_REQUEST]: setEntitySubscribedInstagramMentions,
  [SET_SUBSCRIBE_INSTAGRAM_MENTIONS_SUCCESS]: setEntitySubscribedInstagramMentions,
  [SET_YOUTUBE_ACC_TOKEN_SUCCESS]: setEntitySocialToken,
  [SET_TWITTER_ACC_TOKEN_SUCCESS]: setEntitySocialToken,
  [BLOCK_BOARD_WORDS_REQUEST]: entityBlockWord,
  [UNBLOCK_BOARD_WORDS_REQUEST]: entityUnblockWord,
  [TOGGLE_BOARD_WORD_BLACKLIST_REQUEST]: entityToggleWordBlackList,
  [ADD_FOLDER]: addFolder,
  [SET_FOLDER_SETTINGS_REQUEST]: setFolderSettingsOptimistic,
  [SET_FOLDER_SETTINGS_SUCCESS]: setFolderSettings,
  [SET_FOLDER_META_REQUEST]: setFolderMeta,
  [SET_FOLDER_META_SUCCESS]: setFolderMeta,
  [DELETE_FOLDER_REQUEST]: deleteFolder,
  [DELETE_FOLDER_SUCCESS]: deleteFolder,
  [PUBLISH_BOARD_SUCCESS]: setEntity,
  [SET_BOARD]: setEntity,
  [REQUIRE_BOARD_WORDS_REQUEST]: entityAddRequiredWords,
  [REMOVE_REQUIRED_BOARD_WORD_REQUEST]: entityRemoveRequiredWord,
  [ADD_FOLDER_TO_STORY_SUCCESS]: setAddedCount,
  [REMOVE_FOLDER_FROM_STORY_SUCCESS]: setRemovedCount,
};

export default createReducer(functionMap, initialState);
